import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import RTButton from './RTButton';
import AppliedFilters from './AppliedFilters';
import './RTFilters.css';

/**
 * @typedef {Object} RTFilterOption
 * @property {string|number} value
 * @property {string} label
 */

/**
 * @typedef {Object} RTFilterField
 * @property {string} key
 * @property {string} label
 * @property {'dropdown'|'text'|'checkbox'|'autocomplete'|'date'} type
 * @property {'compact'|'medium'|'wide'} [width]
 * @property {string} [placeholder]
 * @property {RTFilterOption[]} [options]
 * @property {boolean} [applyImmediately] - Defaults to true for dropdown fields; false for other types unless set
 * @property {string} [checkboxText]
 */

const WIDTH_CLASS = {
  compact: 'rt-filter-field--compact',
  medium: 'rt-filter-field--medium',
  wide: 'rt-filter-field--wide',
};

const fieldAppliesImmediately = (field) => {
  if (field.applyImmediately != null) return field.applyImmediately;
  return field.type === 'dropdown';
};

const RTFilters = ({
  title = 'Filter By',
  subtitle,
  fields = [],
  values = {},
  searchValues = {},
  onChange,
  onSearchChange,
  showApplyButton,
  onApply,
  applyButtonLabel = 'Apply Filters',
  pills = [],
  onRemovePill,
  onClearAll,
  className = '',
}) => {
  const [openAutocompleteKey, setOpenAutocompleteKey] = useState(null);
  const displayApplyButton = showApplyButton ?? fields.some(field => !fieldAppliesImmediately(field));

  const handleDropdownChange = (field, rawValue) => {
    if (!onChange) return;
    let value = rawValue === '' ? undefined : rawValue;
    if (field.key === 'month' || field.key === 'year') {
      value = rawValue === '' ? undefined : parseInt(rawValue, 10);
    }
    onChange(field.key, value, { applyImmediately: fieldAppliesImmediately(field) });
  };

  const handleTextChange = (field, text) => {
    onChange?.(field.key, text, { applyImmediately: fieldAppliesImmediately(field) });
  };

  const handleCheckboxChange = (field, checked) => {
    onChange?.(field.key, checked, { applyImmediately: fieldAppliesImmediately(field) });
  };

  const handleAutocompleteInputChange = (field, text) => {
    onSearchChange?.(field.key, text);
    if (!text) {
      onChange?.(field.key, undefined, { applyImmediately: fieldAppliesImmediately(field) });
    }
  };

  const handleAutocompleteSelect = (field, option) => {
    onSearchChange?.(field.key, option.label);
    onChange?.(field.key, option.value, { applyImmediately: fieldAppliesImmediately(field) });
    setOpenAutocompleteKey(null);
  };

  const handleAutocompleteClear = (field) => {
    onSearchChange?.(field.key, '');
    onChange?.(field.key, undefined, { applyImmediately: fieldAppliesImmediately(field) });
    setOpenAutocompleteKey(null);
  };

  const getAutocompleteSuggestions = (field) => {
    const searchText = searchValues[field.key] ?? '';
    const options = field.options || [];
    if (!searchText) return options;
    return options.filter(opt =>
      opt.label.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const renderField = (field) => {
    const widthClass = WIDTH_CLASS[field.width] || WIDTH_CLASS.medium;

    if (field.type === 'dropdown') {
      return (
        <div key={field.key} className={`rt-filter-field ${widthClass}`}>
          <label className="rt-filter-label" htmlFor={`rt-filter-${field.key}`}>
            {field.label}
          </label>
          <select
            id={`rt-filter-${field.key}`}
            className="rt-filter-select"
            value={values[field.key] ?? ''}
            onChange={(e) => handleDropdownChange(field, e.target.value)}
          >
            {(field.options || []).map(option => (
              <option key={String(option.value)} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'date') {
      return (
        <div key={field.key} className={`rt-filter-field ${widthClass}`}>
          <label className="rt-filter-label" htmlFor={`rt-filter-${field.key}`}>
            {field.label}
          </label>
          <DatePicker
            id={`rt-filter-${field.key}`}
            selected={values[field.key] || null}
            onChange={(date) => onChange?.(field.key, date, { applyImmediately: fieldAppliesImmediately(field) })}
            dateFormat="dd/MM/yyyy"
            className="rt-filter-input"
            placeholderText={field.placeholder || 'DD/MM/YYYY'}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            autoComplete="off"
          />
        </div>
      );
    }

    if (field.type === 'text') {
      return (
        <div key={field.key} className={`rt-filter-field ${widthClass}`}>
          <label className="rt-filter-label" htmlFor={`rt-filter-${field.key}`}>
            {field.label}
          </label>
          <input
            id={`rt-filter-${field.key}`}
            type="text"
            className="rt-filter-input"
            placeholder={field.placeholder || ''}
            value={values[field.key] ?? ''}
            onChange={(e) => handleTextChange(field, e.target.value)}
            autoComplete="off"
            name={`rt-filter-${field.key}`}
          />
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <div key={field.key} className="rt-filter-field rt-filter-field--checkbox">
          <label className="rt-filter-checkbox-label">
            <input
              type="checkbox"
              className="rt-filter-checkbox"
              checked={Boolean(values[field.key])}
              onChange={(e) => handleCheckboxChange(field, e.target.checked)}
            />
            <span className="rt-filter-checkbox-text">
              {field.checkboxText || field.label}
            </span>
          </label>
        </div>
      );
    }

    if (field.type === 'autocomplete') {
      const suggestions = getAutocompleteSuggestions(field);
      const hasValue = values[field.key] !== undefined && values[field.key] !== null && values[field.key] !== '';
      const displayText = searchValues[field.key] ?? '';

      return (
        <div
          key={field.key}
          className={`rt-filter-field rt-filter-field--autocomplete ${widthClass}`}
        >
          <label className="rt-filter-label" htmlFor={`rt-filter-${field.key}`}>
            {field.label}
          </label>
          <div className="rt-filter-input-wrap">
            <input
              id={`rt-filter-${field.key}`}
              type="search"
              className="rt-filter-input"
              placeholder={field.placeholder || 'Search...'}
              value={displayText}
              onChange={(e) => handleAutocompleteInputChange(field, e.target.value)}
              onFocus={(e) => {
                e.target.removeAttribute('readonly');
                setOpenAutocompleteKey(field.key);
              }}
              onBlur={() => {
                setTimeout(() => setOpenAutocompleteKey(null), 150);
              }}
              autoComplete="off"
              name={`rt-filter-${field.key}`}
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={openAutocompleteKey === field.key && suggestions.length > 0}
              aria-controls={`rt-filter-${field.key}-listbox`}
              readOnly
            />
            {hasValue && (
              <button
                type="button"
                className="rt-filter-clear-icon"
                onClick={() => handleAutocompleteClear(field)}
                aria-label={`Clear ${field.label} filter`}
              >
                ×
              </button>
            )}
          </div>
          {openAutocompleteKey === field.key && suggestions.length > 0 && (
            <ul
              id={`rt-filter-${field.key}-listbox`}
              className="rt-filter-autocomplete-list"
              role="listbox"
            >
              {suggestions.map(option => (
                <li
                  key={String(option.value)}
                  onMouseDown={() => handleAutocompleteSelect(field, option)}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`rt-filters${className ? ` ${className}` : ''}`}>
      <div className="rt-filters-heading">
        <h3 className="rt-filters-title">
          {title}
          {pills.length > 0 && ` (${pills.length})`}
        </h3>
        {subtitle && (
          <p className="rt-filters-subtitle">{subtitle}</p>
        )}
      </div>

      <div className="rt-filters-row">
        {fields.map(renderField)}
        {displayApplyButton && (
          <div className="rt-filter-actions">
            <RTButton variant="primary" onClick={onApply}>
              {applyButtonLabel}
            </RTButton>
          </div>
        )}
      </div>

      <AppliedFilters
        pills={pills}
        onRemovePill={onRemovePill}
        onClearAll={onClearAll}
      />
    </div>
  );
};

export default RTFilters;
